import React from 'react';
import styles from './ErrorBoundary.module.css';

/**
 * React Error Boundary — catches unhandled JavaScript errors during rendering
 * and displays a user-friendly fallback UI instead of a blank white screen.
 *
 * Class component is required because React does not support Error Boundaries
 * with hooks (as of React 18).
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log to console in development; in production this could go to
    // an error tracking service (e.g., Sentry, LogRocket)
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className={styles.container}>
          <div className={styles.card}>
            <div className={styles.iconWrapper}>
              <svg
                className={styles.icon}
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
                <path d="M12 7v6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <circle cx="12" cy="16.5" r="0.75" fill="currentColor" />
              </svg>
            </div>
            <h1 className={styles.title}>Something went wrong</h1>
            <p className={styles.description}>
              An unexpected error occurred. Please try reloading the page or go back to the home page.
            </p>
            <div className={styles.actions}>
              <button className={styles.primaryButton} onClick={this.handleReload}>
                Reload Page
              </button>
              <button className={styles.secondaryButton} onClick={this.handleGoHome}>
                Go to Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
